import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '../toast/toast.service';
import {environment} from '../../environments/environment';
import {IProjectVersionData} from '../../models/project/project-data';
import {ProjectVersionBumpKind} from "../../utils/question-answer.util";
import * as FileSaver from "file-saver";
import {ApiService} from "../api/api.service";

@Injectable({providedIn: 'root'})
export class ProjectVersionService {
    private readonly httpClient = inject(HttpClient);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly apiService = inject(ApiService);

    public list(projectId: number): Observable<IProjectVersionData[]> {
        return this.httpClient.get<IProjectVersionData[]>(this.baseUrl(projectId)).pipe(
            map((versions) => [...versions]
                .sort((a, b) => b.majorVersion - a.majorVersion || b.minorVersion - a.minorVersion)
            ),
            catchError((error) => this.handleError(error, 'ERRORS.project_versions_not_retrieved'))
        );
    }

    public create(
        projectId: number,
        bumpKind: ProjectVersionBumpKind,
        note: string | null
    ): Observable<IProjectVersionData> {
        return this.httpClient.post<IProjectVersionData>(this.baseUrl(projectId), {
            note,
            type: bumpKind === 'major' ? 'MAJOR' : 'MINOR'
        }).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_version_not_created'))
        );
    }

    public downloadPdf(version: IProjectVersionData): Observable<void> {
        const attachment = version.attachment;
        if (attachment?.uniqueId) {
           const promise = this.apiService.getAttachment(`/attachment/${attachment.uniqueId}`, attachment.contentType)
                .then((blob) => FileSaver.saveAs(blob, version.versionFileName))
                .catch((error) =>
                     this.toastService.openError(error.status ?? 0,
                        this.translateService.instant('NEW_PROJECT_VERSION.toast.download_failed')
                    )
                );
            return from(promise);
        }

        this.toastService.openError(0,
            this.translateService.instant( 'NEW_PROJECT_VERSION.toast.download_not_available')
        );

        return of(undefined as void);
    }

    private baseUrl(projectId: number): string {
        return `${environment.apiUrl}/projects/${projectId}/versions`;
    }

    private handleError(error: HttpErrorResponse, translationKey: string): Observable<never> {
        console.error(error);
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
        return throwError(() => error);
    }
}
